<template>
  <Title />
  <!-- Executive Control Bar -->
  <div
    class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-4 md:p-5 mb-6 shadow-xs transition-all duration-200"
  >
    <div
      class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3.5"
    >
      <!-- Search Input -->
      <div class="flex-1 max-w-md">
        <div class="relative">
          <Search
            class="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search faculty by name, email, phone..."
            class="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
          />
        </div>
      </div>

      <!-- Add Faculty Button -->
      <button
        v-show="role === 'admin' || role === 'superadmin'"
        @click="openAddModal"
        class="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-semibold shadow-xs transition-colors shrink-0 cursor-pointer"
      >
        <Plus class="w-4 h-4" />
        <span>Add Faculty</span>
      </button>
    </div>
  </div>

  <!-- Modal: Add New Faculty -->
  <div
    v-if="showModal"
    class="fixed inset-0 bg-slate-950/70 backdrop-blur-md flex items-center justify-center z-[10000] p-3 sm:p-6 overflow-y-auto"
    @click.self="closeModal"
  >
    <div
      class="bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/90 dark:border-[#263449] w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col transform transition-all duration-300"
    >
      <!-- Modal Loading Overlay -->
      <AppLoader
        :isVisible="isSubmitting"
        variant="card"
        size="md"
        message="Registering Faculty Member..."
        subtext="Uploading profile credentials and qualification certificates"
        color="indigo"
      />

      <!-- Top Subtle Gradient Accent Line -->
      <div class="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600 shrink-0"></div>

      <!-- Professional Modal Header Bar -->
      <div
        class="bg-white dark:bg-[#111C30] px-6 py-4 border-b border-slate-100 dark:border-[#263449] flex items-center justify-between shrink-0"
      >
        <div>
          <h2 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
            Add New Faculty Member
          </h2>
          <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-normal">
            Register educator credentials, contact numbers, and qualifications
          </p>
        </div>

        <button
          @click="closeModal"
          :disabled="isSubmitting"
          class="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors disabled:opacity-50"
          title="Close Modal"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Navigation Segmented Tabs -->
      <div
        class="px-6 py-2 bg-slate-50/80 dark:bg-[#0B1120]/40 border-b border-slate-100 dark:border-[#263449] flex items-center gap-1.5 overflow-x-auto shrink-0"
      >
        <button
          type="button"
          @click="activeTab = 'personal'"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            activeTab === 'personal'
              ? 'bg-white dark:bg-[#18243A] text-slate-900 dark:text-white shadow-2xs border border-slate-200/80 dark:border-[#263449]'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50',
          ]"
        >
          <User class="w-3.5 h-3.5" />
          <span>Personal & Contact</span>
        </button>
        <button
          type="button"
          @click="activeTab = 'qualifications'"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            activeTab === 'qualifications'
              ? 'bg-white dark:bg-[#18243A] text-slate-900 dark:text-white shadow-2xs border border-slate-200/80 dark:border-[#263449]'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800/50',
          ]"
        >
          <GraduationCap class="w-3.5 h-3.5" />
          <span>Educational Qualifications</span>
        </button>
      </div>

      <!-- Form Body -->
      <form
        @submit.prevent="submitForm"
        class="flex flex-col flex-1 overflow-hidden"
      >
        <div class="overflow-y-auto p-5 sm:p-7 space-y-6 flex-1 bg-slate-50/50 dark:bg-[#0B1120]">
          <!-- General Form Error Banner -->
          <div
            v-if="formError"
            class="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-2xl flex items-start gap-3"
          >
            <div class="p-1 bg-rose-100 dark:bg-rose-900/60 rounded-full text-rose-600 dark:text-rose-400 shrink-0">
              <AlertCircle class="w-4 h-4" />
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="text-xs font-bold text-rose-800 dark:text-rose-200">Unable to Submit</h4>
              <p class="text-xs text-rose-700 dark:text-rose-300 mt-0.5 break-words">{{ formError }}</p>
            </div>
            <button
              @click="formError = ''"
              class="text-rose-400 hover:text-rose-600 p-1 rounded-lg"
            >
              <X class="w-4 h-4" />
            </button>
          </div>

          <!-- TAB 1: Personal & Contact Information -->
          <div
            v-show="activeTab === 'personal'"
            class="space-y-6"
          >
            <!-- Core Details Card -->
            <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
              <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <User class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Identity & Contact Details</span>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                <!-- Full Name -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Full Name <span class="text-rose-500">*</span>
                  </label>
                  <input
                    v-model="currentTeacher.name"
                    type="text"
                    class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    :class="fieldErrors.name ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
                    placeholder="e.g. Dr. Jane Smith"
                  />
                  <p v-if="fieldErrors.name" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                    <AlertCircle class="w-3.5 h-3.5" /> {{ fieldErrors.name }}
                  </p>
                </div>

                <!-- Email -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Email Address <span class="text-rose-500">*</span>
                  </label>
                  <input
                    v-model="currentTeacher.email"
                    type="email"
                    class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    :class="fieldErrors.email ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
                    placeholder="teacher@school.edu"
                  />
                  <p v-if="fieldErrors.email" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                    <AlertCircle class="w-3.5 h-3.5" /> {{ fieldErrors.email }}
                  </p>
                </div>

                <!-- Mobile Phone -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Mobile Number <span class="text-rose-500">*</span>
                  </label>
                  <input
                    v-model="currentTeacher.mobile"
                    type="tel"
                    maxlength="10"
                    class="w-full px-3.5 py-2.5 border rounded-xl text-xs sm:text-sm font-mono font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    :class="fieldErrors.mobile ? 'border-rose-300 bg-rose-50/50' : 'border-slate-300 dark:border-[#263449]'"
                    placeholder="10-digit mobile number"
                  />
                  <p v-if="fieldErrors.mobile" class="text-xs text-rose-600 dark:text-rose-400 mt-1 flex items-center gap-1">
                    <AlertCircle class="w-3.5 h-3.5" /> {{ fieldErrors.mobile }}
                  </p>
                </div>

                <!-- Status -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Status <span class="text-rose-500">*</span>
                  </label>
                  <select
                    v-model="currentTeacher.status"
                    class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  >
                    <option value="active">Active Faculty</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Profile Photo Upload Card -->
            <div class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4">
              <div class="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-xs uppercase tracking-wider pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <Camera class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>Profile Portrait (Optional)</span>
              </div>

              <div class="flex flex-col sm:flex-row items-center gap-5">
                <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden ring-4 ring-slate-100 dark:ring-slate-800 shadow-md shrink-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <img
                    v-if="profilePhotoPreview"
                    :src="profilePhotoPreview"
                    alt="Preview"
                    class="w-full h-full object-cover"
                  />
                  <User v-else class="w-10 h-10 text-slate-400" />
                </div>

                <div class="flex-1 space-y-2 w-full text-center sm:text-left">
                  <div class="flex items-center gap-2 justify-center sm:justify-start">
                    <label class="cursor-pointer inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold border border-indigo-200 dark:border-indigo-800 transition">
                      <Upload class="w-3.5 h-3.5" />
                      <span>Select Photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleProfilePhotoUpload"
                        class="hidden"
                      />
                    </label>
                    <button
                      v-if="profilePhotoFile"
                      type="button"
                      @click="removeProfilePhotoFile"
                      class="px-3 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl text-xs font-semibold transition"
                    >
                      Remove
                    </button>
                  </div>
                  <p class="text-[11px] text-slate-400 dark:text-slate-500">
                    JPG or PNG portrait image (Max 5MB)
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- TAB 2: Educational Qualifications -->
          <div
            v-show="activeTab === 'qualifications'"
            class="space-y-4"
          >
            <div
              v-for="(qualification, index) in qualifications"
              :key="index"
              class="bg-white dark:bg-[#111C30] p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-xs space-y-4"
            >
              <div class="flex justify-between items-center pb-3 border-b border-slate-100 dark:border-[#1E293B]">
                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-white flex items-center gap-2">
                  <GraduationCap class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span>Qualification {{ index + 1 }}</span>
                </h4>
                <button
                  v-if="qualifications.length > 1"
                  type="button"
                  @click="removeQualification(index)"
                  class="text-rose-600 hover:text-rose-800 p-1 rounded-lg text-xs font-semibold flex items-center gap-1"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                  <span>Remove</span>
                </button>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <!-- Type -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Degree / Level <span class="text-rose-500">*</span>
                  </label>
                  <select
                    v-model="qualification.type"
                    class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                  >
                    <option value="">Select Degree Type</option>
                    <option value="Matric">Matric (10th)</option>
                    <option value="Higher Secondary">Higher Secondary (12th)</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="PhD">PhD / Doctorate</option>
                    <option value="B.Ed">B.Ed</option>
                    <option value="Other">Other Certificate</option>
                  </select>
                </div>

                <!-- Institution -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Institution / University <span class="text-rose-500">*</span>
                  </label>
                  <input
                    v-model="qualification.institution"
                    type="text"
                    class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    placeholder="Institution or board"
                  />
                </div>

                <!-- Year of Passing -->
                <div>
                  <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Year of Passing <span class="text-rose-500">*</span>
                  </label>
                  <input
                    v-model="qualification.year"
                    type="number"
                    min="1960"
                    :max="new Date().getFullYear()"
                    class="w-full px-3.5 py-2.5 border border-slate-300 dark:border-[#263449] rounded-xl text-xs sm:text-sm font-medium bg-white dark:bg-[#18243A] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    placeholder="YYYY"
                  />
                </div>
              </div>

              <!-- Certificate File Upload -->
              <div class="pt-2">
                <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  Certificate Document (PDF/Image)
                </label>
                <div class="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    @change="(e) => handleQualificationUpload(e, index)"
                    class="w-full text-xs text-slate-500 file:mr-2.5 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  <button
                    v-if="qualification.file"
                    type="button"
                    @click="removeQualificationFile(index)"
                    class="text-xs text-rose-600 hover:text-rose-800 font-semibold shrink-0"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <!-- Add Another Button -->
            <button
              type="button"
              @click="addQualification"
              class="w-full py-3 border-2 border-dashed border-slate-300 dark:border-[#263449] rounded-2xl text-center text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/30 transition flex items-center justify-center gap-1.5"
            >
              <Plus class="w-4 h-4" />
              <span>Add Another Qualification</span>
            </button>
          </div>
        </div>

        <!-- Sticky Action Footer -->
        <div
          class="p-4 sm:p-5 border-t border-slate-200/80 dark:border-[#263449] bg-white dark:bg-[#111C30] flex items-center justify-between shrink-0"
        >
          <span class="text-[11px] text-slate-400 dark:text-slate-500 hidden sm:inline">
            Required fields are marked with <span class="text-rose-500 font-bold">*</span>
          </span>
          <div class="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            <button
              type="button"
              @click="closeModal"
              :disabled="isSubmitting"
              class="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-semibold shadow-sm transition disabled:opacity-50"
            >
              <Plus class="w-3.5 h-3.5" />
              <span>{{ isSubmitting ? "Saving..." : "Add Faculty" }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, watch, computed } from "vue";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import { useStore } from "vuex";
  import {
    Search,
    Plus,
    X,
    Upload,
    Trash2,
    AlertCircle,
    User,
    GraduationCap,
    Camera,
  } from "lucide-vue-next";
  import Title from "@/components/Teacher/Title.vue";

  const emit = defineEmits(["search"]);

  const props = defineProps({
    bannerGoClicked: {
      type: Boolean,
      default: false,
    },
  });

  const store = useStore();
  const { showToast } = useGlobalToast();

  const searchQuery = ref("");
  const showModal = ref(false);
  const isSubmitting = ref(false);
  const activeTab = ref("personal");

  const role = computed(() => store.getters.getUserRole);
  const TotalTeachers = computed(
    () => store.getters.getOnboardingTotalTeachers || 0,
  );

  const formError = ref("");
  const fieldErrors = reactive({});

  const profilePhotoFile = ref(null);
  const profilePhotoPreview = ref(null);

  const currentTeacher = reactive({
    name: "",
    email: "",
    mobile: "",
    status: "active",
  });

  const qualifications = ref([
    {
      type: "",
      institution: "",
      year: "",
      file: null,
    },
  ]);

  watch(searchQuery, (newQuery) => {
    emit("search", newQuery);
  });

  watch(
    () => props.bannerGoClicked,
    (newVal) => {
      if (newVal) {
        showModal.value = true;
      }
    },
  );

  const openAddModal = () => {
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    currentTeacher.name = "";
    currentTeacher.email = "";
    currentTeacher.mobile = "";
    currentTeacher.status = "active";
    profilePhotoFile.value = null;
    profilePhotoPreview.value = null;
    qualifications.value = [
      {
        type: "",
        institution: "",
        year: "",
        file: null,
      },
    ];
    formError.value = "";
    for (const key in fieldErrors) {
      delete fieldErrors[key];
    }
  };

  const refreshTeachers = async () => {
    try {
      await store.dispatch("fetchTeachers", {
        page: 1,
        limit: 20,
        search: "",
      });
      await store.dispatch("fetchTotalTeacherCountOnboarding");
    } catch (error) {
      console.error("Error refreshing teachers:", error);
    }
  };

  const handleProfilePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast("error", "Profile photo size should be less than 5MB.");
      return;
    }
    profilePhotoFile.value = file;
    profilePhotoPreview.value = URL.createObjectURL(file);
    if (fieldErrors.profilePhotoFile) {
      delete fieldErrors.profilePhotoFile;
    }
  };

  const removeProfilePhotoFile = () => {
    profilePhotoFile.value = null;
    profilePhotoPreview.value = null;
  };

  const addQualification = () => {
    qualifications.value.push({
      type: "",
      institution: "",
      year: "",
      file: null,
    });
  };

  const removeQualification = (index) => {
    qualifications.value.splice(index, 1);
  };

  const handleQualificationUpload = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast("error", "Certificate file size should be under 5MB.");
      return;
    }
    qualifications.value[index].file = file;
  };

  const removeQualificationFile = (index) => {
    qualifications.value[index].file = null;
  };

  const validateForm = () => {
    let isValid = true;
    formError.value = "";
    for (const key in fieldErrors) {
      delete fieldErrors[key];
    }

    if (!currentTeacher.name?.trim()) {
      fieldErrors.name = "Full Name is required.";
      isValid = false;
      activeTab.value = "personal";
    }

    if (!currentTeacher.email?.trim()) {
      fieldErrors.email = "Email is required.";
      isValid = false;
      activeTab.value = "personal";
    } else if (!/\S+@\S+\.\S+/.test(currentTeacher.email)) {
      fieldErrors.email = "Invalid email format.";
      isValid = false;
      activeTab.value = "personal";
    }

    if (!currentTeacher.mobile?.trim()) {
      fieldErrors.mobile = "Mobile Number is required.";
      isValid = false;
      activeTab.value = "personal";
    } else if (!/^\d{10}$/.test(currentTeacher.mobile)) {
      fieldErrors.mobile = "Mobile Number must be 10 digits.";
      isValid = false;
      activeTab.value = "personal";
    }

    return isValid;
  };

  const submitForm = async () => {
    if (!validateForm()) {
      formError.value = "Please fill in all required fields.";
      showToast("error", "Please correct the form errors.");
      return;
    }

    isSubmitting.value = true;
    try {
      const formData = new FormData();
      formData.append("name", currentTeacher.name?.trim());
      formData.append("email", currentTeacher.email?.trim());
      formData.append("phone", currentTeacher.mobile?.trim());
      if (profilePhotoFile.value) {
        formData.append("photo", profilePhotoFile.value);
      }
      formData.append("role", "teacher");
      if (currentTeacher.status) {
        formData.append("status", currentTeacher.status);
      }

      const qualificationPayload = qualifications.value
        .map((q) => ({
          type: q.type?.trim(),
          institution: q.institution?.trim(),
          year: q.year ? String(q.year).trim() : "",
        }))
        .filter((q) => q.type || q.institution || q.year);

      if (qualificationPayload.length > 0) {
        formData.append("qualifications", JSON.stringify(qualificationPayload));
      }

      qualificationPayload.forEach((q, index) => {
        const original = qualifications.value[index];
        if (original?.file instanceof File) {
          formData.append(`qualification_${index}_file`, original.file);
        }
      });

      await store.dispatch("addTeacher", formData);
      showToast("success", "Faculty member added successfully.");
      await refreshTeachers();
      closeModal();
    } catch (error) {
      console.error("Upload error:", error.response?.data || error);
      let errorMessage = "Registration failed. Please check the inputs.";
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, message]) => {
          fieldErrors[field] = message;
        });
        errorMessage = Object.values(error.response.data.errors).join(" | ");
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      formError.value = errorMessage;
      showToast("error", errorMessage);
    } finally {
      isSubmitting.value = false;
    }
  };

  watch(
    TotalTeachers,
    (newVal) => {
      if (newVal === 0) {
        showModal.value = true;
      }
    },
    { immediate: true },
  );
</script>
